import type { Account, WalletGroupedAccounts } from '@polaris/core/models'
import { clearAllSessionData } from '@polaris/core/stores/mpc/atom'
import { atomsGlobalStore } from '@polaris/core/utils'
import {
  type MutationState,
  useMutation,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query'
import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useMemo } from 'react'
import { ensureDeleteWebServerCookieForLocalhost } from '@/src/services/auth/localhost'
import { logout } from '@/src/services/auth/logout'
import { updateAccount } from '@/src/services/user'
import {
  accountsAtom,
  isLocalStateModeAtom,
  localAccountsAtom,
} from '@/src/stores/accounts/atoms'
import { walletOptionsAtom } from '../wallets/atoms'

// TODO: Replace with actual query client and query key
// Temporary mock: always returns an object with an accounts array for linter compatibility
const mockQueryClient = {
  getQueryData: (_: unknown) => ({ accounts: [] }),
  setQueryData: (_: unknown, _data: unknown) => {},
}
const mockQueryKey = 'REPLACE_ME_QUERY_KEY'

interface UseEditAccountEmailData {
  email: string
}

export const useEditAccountEmailState = () => {
  const states = useMutationState<
    MutationState<unknown, Error, UseEditAccountEmailData>
  >({
    filters: {
      mutationKey: ['edit-account-email'],
    },
  })
  return useMemo(() => [...states].pop(), [states])
}

export const useEditAccountEmail = () => {
  return useMutation({
    mutationKey: ['edit-account-email'],
    mutationFn: async (data: UseEditAccountEmailData) => {
      const currentProfile = mockQueryClient.getQueryData(mockQueryKey)
      if (!currentProfile) {
        console.warn(
          'No profile found when editing account name, skipping edit-account-email mutation',
        )
        return
      }
      const updatedProfile = {
        ...currentProfile,
        email: data.email,
      }
      mockQueryClient.setQueryData(mockQueryKey, updatedProfile)
      return updatedProfile
    },
  })
}

export const useEditAccountName = () => {
  return useMutation({
    mutationKey: ['edit-account-name'],
    mutationFn: async (data: { name: string }) => {
      const currentProfile = mockQueryClient.getQueryData(mockQueryKey)
      if (!currentProfile) {
        console.warn(
          'No profile found when editing account name, skipping edit-account-name mutation',
        )
        return
      }
      const updatedProfile = {
        ...currentProfile,
        name: data.name,
      }
      mockQueryClient.setQueryData(mockQueryKey, updatedProfile)
      return updatedProfile
    },
  })
}

interface UseEditWalletNameData {
  account: WalletGroupedAccounts
  name: string
}

export const useEditWalletName = () => {
  const queryClient = useQueryClient()
  const accounts = useAtomValue(accountsAtom)
  const isLocalState = useAtomValue(isLocalStateModeAtom)
  const setLocalAccounts = useSetAtom(localAccountsAtom)

  const editWalletName = useMutation({
    mutationKey: ['edit-wallet-name'],
    mutationFn: async (data: UseEditWalletNameData) => {
      const cleanedName = data.name.trim()
      if (!cleanedName) {
        throw new Error('Wallet name cannot be empty')
      }

      // Get all account addresses in this wallet group
      const groupAddresses = new Set(
        data.account.accounts.map((a) => a.address),
      )

      // GUEST MODE: Update localStorage
      if (isLocalState) {
        const updatedAccounts = accounts.map((acc) => {
          if (groupAddresses.has(acc.address)) {
            return { ...acc, name: cleanedName }
          }
          return acc
        })
        setLocalAccounts(updatedAccounts)
        return
      }

      // LOGGED-IN MODE: Update backend
      const serverAccountsToPatch = accounts.filter(
        (acc) => groupAddresses.has(acc.address) && !!acc.id,
      )

      if (serverAccountsToPatch.length === 0) {
        console.warn('No server accounts found to update for this wallet group')
        return
      }

      // Optimistically update the cache immediately for instant UI update
      queryClient.setQueryData(
        ['user', 'accounts'],
        (oldData: Account[] | undefined) => {
          if (!oldData) return oldData
          return oldData.map((acc) =>
            groupAddresses.has(acc.address)
              ? { ...acc, name: cleanedName }
              : acc,
          )
        },
      )

      // Patch all accounts in the wallet group with the new name
      await Promise.all(
        serverAccountsToPatch.map((account) =>
          updateAccount(account.id as string, cleanedName),
        ),
      )

      // Refetch to ensure data is in sync with server
      await queryClient.refetchQueries({
        queryKey: ['user', 'accounts'],
      })
    },
  })

  return editWalletName
}

/**
 * Logs out the user and clears the local accounts and MPC session data
 *
 * @returns A function that logs out the user and clears the local accounts and MPC session data
 */
export function useLogout() {
  const queryClient = useQueryClient()

  return useCallback(async () => {
    // We cannot access this directly due to this hook potentially being instantiated
    // outside the jotai provider
    const { disconnectAll } = atomsGlobalStore.get(walletOptionsAtom)
    // Disconnect all wallets
    disconnectAll()

    // Clear guest mode accounts
    atomsGlobalStore.set(localAccountsAtom, [])

    // Clear MPC session data
    clearAllSessionData()

    // Invalidate MPC wallet addresses query to remove cached addresses
    await queryClient.invalidateQueries({
      queryKey: ['mpc', 'wallet-addresses'],
    })

    // Call logout API
    await logout()

    // Handle localhost cookie cleanup
    await ensureDeleteWebServerCookieForLocalhost()
  }, [queryClient])
}
